import { API_CONFIG } from '../config';
import { getParachuteAuthToken } from '../utils/parachuteAuthToken';

/** Admin UI calls this “client licenses”; REST resource is `/api/pct/v1/lawfirms` (LawFirm). */
export interface ClientLicenseDto {
  id: string;
  firmName: string;
  contactEmail: string;
  phone: string | null;
  address: string | null;
  maxAnalyses: number;
  expiresAt: string | null;
  active: boolean;
  adminFirstName?: string | null;
  adminLastName?: string | null;
}

let fetchClientLicensesInFlight: Promise<ClientLicenseDto[]> | null = null;

function authHeaders(): HeadersInit {
  const token = getParachuteAuthToken();
  return {
    accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

const NO_STORE: RequestInit = { cache: 'no-store' };

/** Normalize API expiry: camelCase or PascalCase, ISO date or date-time string, or DateOnly JSON object. */
function parseExpiresAt(raw: Record<string, unknown>): string | null {
  const v = raw.expiresAt ?? raw.ExpiresAt;
  if (v == null || v === '') return null;
  if (typeof v === 'string') {
    const t = v.trim();
    if (!t) return null;
    const ymd = t.match(/^(\d{4}-\d{2}-\d{2})/);
    return ymd ? ymd[1] : null;
  }
  if (typeof v === 'object' && v !== null) {
    const o = v as { year?: unknown; month?: unknown; day?: unknown };
    if (typeof o.year === 'number' && typeof o.month === 'number' && typeof o.day === 'number') {
      const y = o.year;
      const m = String(o.month).padStart(2, '0');
      const d = String(o.day).padStart(2, '0');
      return `${y}-${m}-${d}`;
    }
  }
  return null;
}

function normalizeLicense(raw: Record<string, unknown>): ClientLicenseDto {
  const phoneRaw = raw.phone ?? raw.Phone;
  const addressRaw = raw.address ?? raw.Address;
  const af = raw.adminFirstName ?? raw.AdminFirstName;
  const al = raw.adminLastName ?? raw.AdminLastName;
  return {
    id: String(raw.id ?? raw.Id ?? ''),
    firmName: String(raw.firmName ?? raw.FirmName ?? ''),
    contactEmail: String(raw.contactEmail ?? raw.ContactEmail ?? ''),
    phone: phoneRaw != null && phoneRaw !== '' ? String(phoneRaw) : null,
    address: addressRaw != null && addressRaw !== '' ? String(addressRaw) : null,
    maxAnalyses: Number(raw.maxAnalyses ?? raw.MaxAnalyses ?? raw.maxCompanies ?? raw.MaxCompanies ?? 0) || 0,
    expiresAt: parseExpiresAt(raw),
    active: Boolean(raw.active ?? raw.Active),
    adminFirstName: af != null && String(af).trim() !== '' ? String(af) : null,
    adminLastName: al != null && String(al).trim() !== '' ? String(al) : null,
  };
}

/** Current law firm for the logged-in firm user (`GET /api/pct/v1/workspace/lawfirm`). */
export async function fetchWorkspaceLawFirm(): Promise<ClientLicenseDto> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.WORKSPACE_LAWFIRM}`;
  const response = await fetch(url, { ...NO_STORE, headers: authHeaders() });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Failed to load law firm (${response.status})`);
  }
  const json = (await response.json()) as { data?: unknown };
  const raw = json?.data;
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid law firm response');
  }
  return normalizeLicense(raw as Record<string, unknown>);
}

/** Row from `GET /api/pct/v1/workspace/lawfirm/users` (firm workspace users: this firm’s LawFirmId and login type firm). */
export interface LawFirmUserRow {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  role: string;
  loginType: string;
  isActive: boolean;
  createdAt: string;
}

export type UserOnboardingInvite = {
  temporaryPassword: string;
  passwordResetLink: string;
};

export type CreateWorkspaceLawFirmUserResult = {
  user: LawFirmUserRow;
  invite: UserOnboardingInvite;
};

export function normalizeLawFirmUser(raw: Record<string, unknown>): LawFirmUserRow {
  const phoneRaw = raw.phone ?? raw.Phone;
  const created = raw.createdAt ?? raw.CreatedAt;
  let createdAt = '';
  if (typeof created === 'string') {
    createdAt = created;
  } else if (created instanceof Date) {
    createdAt = created.toISOString();
  }
  return {
    id: Number(raw.id ?? raw.Id ?? 0) || 0,
    email: String(raw.email ?? raw.Email ?? ''),
    firstName: String(raw.firstName ?? raw.FirstName ?? ''),
    lastName: String(raw.lastName ?? raw.LastName ?? ''),
    phone: phoneRaw != null && String(phoneRaw).trim() !== '' ? String(phoneRaw) : null,
    role: String(raw.role ?? raw.Role ?? ''),
    loginType: String(raw.loginType ?? raw.LoginType ?? ''),
    isActive: Boolean(raw.isActive ?? raw.IsActive),
    createdAt,
  };
}

export async function fetchWorkspaceLawFirmUsers(): Promise<LawFirmUserRow[]> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.WORKSPACE_LAWFIRM_USERS}`;
  const response = await fetch(url, { ...NO_STORE, method: 'GET', headers: authHeaders() });
  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(text || `Failed to load users (${response.status})`);
  }
  const json = (await response.json()) as { data?: unknown };
  const rows = Array.isArray(json?.data) ? json.data : [];
  return rows.map((r) =>
    typeof r === 'object' && r !== null ? normalizeLawFirmUser(r as Record<string, unknown>) : normalizeLawFirmUser({})
  );
}

export async function createWorkspaceLawFirmUser(body: {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  firmRole: 'Admin' | 'Staff';
}): Promise<CreateWorkspaceLawFirmUserResult> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.WORKSPACE_LAWFIRM_USERS}`;
  const response = await fetch(url, {
    ...NO_STORE,
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email: body.email.trim(),
      firstName: body.firstName.trim(),
      lastName: body.lastName.trim(),
      firmRole: body.firmRole,
      ...(body.phone != null && String(body.phone).trim() !== '' ? { phone: String(body.phone).trim() } : {}),
    }),
  });
  if (!response.ok) {
    const errJson = (await response.json().catch(() => null)) as Record<string, unknown> | null;
    let msg = `Failed to invite firm member (${response.status})`;
    if (response.status === 403) {
      msg = 'You do not have permission to invite firm members.';
    } else if (errJson && typeof errJson === 'object') {
      if ('message' in errJson && errJson.message != null) {
        msg = String(errJson.message);
      } else if ('title' in errJson && errJson.title != null) {
        msg = String(errJson.title);
      }
    }
    throw new Error(msg);
  }
  const json = (await response.json()) as { data?: Record<string, unknown> };
  const raw = json?.data;
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid invite firm member response');
  }
  const userRaw = raw.user ?? raw.User;
  const invRaw = raw.invite ?? raw.Invite;
  if (!userRaw || typeof userRaw !== 'object' || !invRaw || typeof invRaw !== 'object') {
    throw new Error('Invalid invite firm member response');
  }
  const inv = invRaw as Record<string, unknown>;
  const tp = inv.temporaryPassword ?? inv.TemporaryPassword;
  const pl = inv.passwordResetLink ?? inv.PasswordResetLink;
  if (typeof tp !== 'string' || typeof pl !== 'string') {
    throw new Error('Invalid invite firm member response');
  }
  return {
    user: normalizeLawFirmUser(userRaw as Record<string, unknown>),
    invite: { temporaryPassword: tp, passwordResetLink: pl },
  };
}

/** `PUT /api/pct/v1/workspace/lawfirm/users/update` — user id is in the JSON body. */
export async function updateWorkspaceLawFirmUser(
  id: number,
  body: {
    firstName: string;
    lastName: string;
    phone?: string;
    firmRole: 'Admin' | 'Staff';
    isActive: boolean;
  }
): Promise<LawFirmUserRow> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.WORKSPACE_LAWFIRM_USERS_UPDATE}`;
  const response = await fetch(url, {
    ...NO_STORE,
    method: 'PUT',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      id,
      firstName: body.firstName.trim(),
      lastName: body.lastName.trim(),
      firmRole: body.firmRole,
      isActive: body.isActive,
      ...(body.phone != null && String(body.phone).trim() !== '' ? { phone: String(body.phone).trim() } : {}),
    }),
  });
  if (!response.ok) {
    const errJson = (await response.json().catch(() => null)) as Record<string, unknown> | null;
    let msg = `Failed to update user (${response.status})`;
    if (response.status === 403) {
      msg = 'You do not have permission to update users.';
    } else if (errJson && typeof errJson === 'object') {
      if ('message' in errJson && errJson.message != null) {
        msg = String(errJson.message);
      } else if ('title' in errJson && errJson.title != null) {
        msg = String(errJson.title);
      }
    }
    throw new Error(msg);
  }
  const json = (await response.json()) as { data?: Record<string, unknown> };
  const raw = json?.data;
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid update user response');
  }
  return normalizeLawFirmUser(raw);
}

/** `POST /api/pct/v1/workspace/lawfirm/users/reset-password` — user id is in the JSON body; returns reset link. */
export async function resetWorkspaceLawFirmUserPassword(id: number): Promise<{ passwordResetLink: string }> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.WORKSPACE_LAWFIRM_USERS_RESET_PASSWORD}`;
  const response = await fetch(url, {
    ...NO_STORE,
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ userId: id }),
  });
  if (!response.ok) {
    const errJson = (await response.json().catch(() => null)) as Record<string, unknown> | null;
    let msg = `Failed to reset password (${response.status})`;
    if (response.status === 403) {
      msg = 'You do not have permission to reset passwords.';
    } else if (errJson && typeof errJson === 'object') {
      if ('message' in errJson && errJson.message != null) {
        msg = String(errJson.message);
      } else if ('title' in errJson && errJson.title != null) {
        msg = String(errJson.title);
      }
    }
    throw new Error(msg);
  }
  const json = (await response.json()) as { data?: Record<string, unknown> };
  const d = json?.data;
  const link = d && typeof d === 'object' ? (d.passwordResetLink ?? d.PasswordResetLink) : null;
  if (typeof link !== 'string' || !link) {
    throw new Error('Invalid reset password response');
  }
  return { passwordResetLink: link };
}

/** Platform admin: issue reset link for the law firm primary admin (contact email). */
export async function issueLawFirmAdminPasswordReset(lawFirmId: string): Promise<{ passwordResetLink: string }> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.lawFirmAdminPasswordReset(lawFirmId)}`;
  const response = await fetch(url, {
    ...NO_STORE,
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
  });
  if (!response.ok) {
    const errJson = (await response.json().catch(() => null)) as Record<string, unknown> | null;
    let msg = `Failed to issue reset link (${response.status})`;
    if (response.status === 403) {
      msg = 'You do not have permission to reset this password.';
    } else if (errJson && typeof errJson === 'object') {
      if ('message' in errJson && errJson.message != null) {
        msg = String(errJson.message);
      }
    }
    throw new Error(msg);
  }
  const json = (await response.json()) as { data?: Record<string, unknown> };
  const d = json?.data;
  const link = d && typeof d === 'object' ? (d.passwordResetLink ?? d.PasswordResetLink) : null;
  if (typeof link !== 'string' || !link) {
    throw new Error('Invalid reset link response');
  }
  return { passwordResetLink: link };
}

export async function fetchClientLicenses(): Promise<ClientLicenseDto[]> {
  if (fetchClientLicensesInFlight) {
    return fetchClientLicensesInFlight;
  }

  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.LAWFIRMS}`;
  fetchClientLicensesInFlight = (async () => {
    const response = await fetch(url, { ...NO_STORE, headers: authHeaders() });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(text || `Failed to load licenses (${response.status})`);
    }
    const json = (await response.json()) as { data?: unknown[] };
    const rows = Array.isArray(json?.data) ? json.data : [];
    return rows.map((r) => normalizeLicense(r as Record<string, unknown>));
  })();

  try {
    return await fetchClientLicensesInFlight;
  } finally {
    fetchClientLicensesInFlight = null;
  }
}

export async function createClientLicense(body: {
  firmName: string;
  contactEmail: string;
  phone: string;
  address: string;
  maxAnalyses: number;
  expiresAt: string | null;
  adminPassword: string;
  adminFirstName?: string;
  adminLastName?: string;
}): Promise<ClientLicenseDto> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.LAWFIRMS}`;
  const exp = body.expiresAt != null ? String(body.expiresAt).trim() : '';
  const ymd = exp.match(/^(\d{4}-\d{2}-\d{2})/);
  const expiresAtPayload = ymd ? ymd[1] : null;

  const response = await fetch(url, {
    ...NO_STORE,
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      firmName: body.firmName,
      contactEmail: body.contactEmail,
      phone: body.phone,
      address: body.address,
      maxAnalyses: body.maxAnalyses,
      expiresAt: expiresAtPayload,
      adminPassword: body.adminPassword,
      ...(body.adminFirstName != null && body.adminFirstName !== '' ? { adminFirstName: body.adminFirstName } : {}),
      ...(body.adminLastName != null && body.adminLastName !== '' ? { adminLastName: body.adminLastName } : {}),
    }),
  });
  if (!response.ok) {
    const errJson = (await response.json().catch(() => null)) as Record<string, unknown> | null;
    let msg = `Failed to create license (${response.status})`;
    if (errJson && typeof errJson === 'object') {
      if ('message' in errJson && errJson.message != null) {
        msg = String(errJson.message);
      } else if ('title' in errJson && errJson.title != null) {
        msg = String(errJson.title);
      }
    }
    throw new Error(msg);
  }
  const json = (await response.json()) as { data?: Record<string, unknown> };
  const raw = json?.data;
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid create license response');
  }
  return normalizeLicense(raw);
}

export async function updateClientLicense(
  id: string,
  body: {
    firmName: string;
    contactEmail: string;
    phone: string;
    address: string;
    maxAnalyses: number;
    expiresAt: string | null;
    adminPassword?: string;
    adminFirstName?: string;
    adminLastName?: string;
  }
): Promise<ClientLicenseDto> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.lawFirm(id)}`;
  const exp = body.expiresAt != null ? String(body.expiresAt).trim() : '';
  const ymd = exp.match(/^(\d{4}-\d{2}-\d{2})/);
  const expiresAtPayload = ymd ? ymd[1] : null;

  const payload: Record<string, unknown> = {
    firmName: body.firmName,
    contactEmail: body.contactEmail,
    phone: body.phone,
    address: body.address,
    maxAnalyses: body.maxAnalyses,
    expiresAt: expiresAtPayload,
  };
  if (body.adminPassword != null && body.adminPassword !== '') {
    payload.adminPassword = body.adminPassword;
  }
  if (body.adminFirstName != null && body.adminFirstName !== '') {
    payload.adminFirstName = body.adminFirstName;
  }
  if (body.adminLastName != null && body.adminLastName !== '') {
    payload.adminLastName = body.adminLastName;
  }

  const response = await fetch(url, {
    ...NO_STORE,
    method: 'PUT',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });
  if (response.status === 404) {
    throw new Error('License not found');
  }
  if (!response.ok) {
    const errJson = (await response.json().catch(() => null)) as Record<string, unknown> | null;
    let msg = `Failed to update license (${response.status})`;
    if (errJson && typeof errJson === 'object') {
      if ('message' in errJson && errJson.message != null) {
        msg = String(errJson.message);
      } else if ('title' in errJson && errJson.title != null) {
        msg = String(errJson.title);
      }
    }
    throw new Error(msg);
  }
  const json = (await response.json()) as { data?: Record<string, unknown> };
  const raw = json?.data;
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid update license response');
  }
  return normalizeLicense(raw);
}

/** Issues a 7-day password reset link for the firm admin (contact email). */
export async function issueClientLicenseAdminPasswordReset(
  license: ClientLicenseDto
): Promise<{ passwordResetLink: string }> {
  return issueLawFirmAdminPasswordReset(license.id);
}

export async function setClientLicenseActive(id: string, active: boolean): Promise<ClientLicenseDto> {
  const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.lawFirmActive(id)}`;
  const response = await fetch(url, {
    ...NO_STORE,
    method: 'PUT',
    headers: {
      ...authHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ active }),
  });
  if (response.status === 404) {
    throw new Error('License not found');
  }
  if (!response.ok) {
    throw new Error(`Failed to update license (${response.status})`);
  }
  const json = (await response.json()) as { data?: Record<string, unknown> };
  const raw = json?.data;
  if (!raw || typeof raw !== 'object') {
    throw new Error('Invalid update license response');
  }
  return normalizeLicense(raw);
}

/** Back-compat: older `*MeLawFirm*` names (same functions as `*WorkspaceLawFirm*`). */
export {
  fetchWorkspaceLawFirm as fetchMeLawFirm,
  fetchWorkspaceLawFirmUsers as fetchMeLawFirmUsers,
  createWorkspaceLawFirmUser as createMeLawFirmUser,
  updateWorkspaceLawFirmUser as updateMeLawFirmUser,
  resetWorkspaceLawFirmUserPassword as resetMeLawFirmUserPassword,
};
